import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Factory, HardHat } from "lucide-react";
import {
  buildWeldMap,
  evaluateTest,
  isWelded,
  NDE_LEVELS,
  TEST_MEDIA,
  weldTotals,
  type NdePercent,
  type TestMedium,
  type TestPackage,
  type WeldPosition,
} from "@/lib/pipe-welds";
import type { MaterialId } from "@/lib/pipe-spec";
import type {
  ComponentPlacement,
  RunFitting,
  TerminationPlacement,
} from "@/hooks/use-pipe-sketch";

interface Props {
  material: MaterialId;
  fittings: RunFitting[];
  placements: ComponentPlacement[];
  terminations: TerminationPlacement[];
  weldPositions: Record<string, WeldPosition>;
  shopFabDefault: boolean;
  test: TestPackage;
  onSetWeldPosition: (id: string, position: WeldPosition) => void;
  onSetAllWelds: (ids: string[], position: WeldPosition) => void;
  onSetShopFabDefault: (v: boolean) => void;
  onSetTest: (patch: Partial<TestPackage>) => void;
}

const WeldMapPanel = ({
  material,
  fittings,
  placements,
  terminations,
  weldPositions,
  shopFabDefault,
  test,
  onSetWeldPosition,
  onSetAllWelds,
  onSetShopFabDefault,
  onSetTest,
}: Props) => {
  const welds = useMemo(
    () =>
      buildWeldMap({
        material,
        fittings: fittings.map((f) => ({
          runId: f.runId,
          index: f.index,
          size: f.size,
          label: f.label,
          welds: f.welds,
        })),
        placements: placements.map((p) => ({
          runId: p.runId,
          index: p.index,
          size: p.size,
          label: p.label,
          joints: p.joints,
        })),
        terminations: terminations.map((t) => ({
          runId: t.runId,
          size: t.size,
          label: t.label,
          joints: t.joints,
        })),
        positions: weldPositions,
        shopFabDefault,
      }),
    [material, fittings, placements, terminations, weldPositions, shopFabDefault],
  );

  const totals = useMemo(() => weldTotals(welds), [welds]);
  const result = useMemo(() => evaluateTest(test, welds), [test, welds]);
  const welded = isWelded(material);
  const ids = welds.map((w) => w.id);

  return (
    <div className="space-y-4 p-3">
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Weld map</h3>
          <span className="text-xs text-muted-foreground">
            {totals.count} joints · {totals.di.toFixed(1)} DI
          </span>
        </div>

        {welds.length === 0 ? (
          <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            Draw a run with a turn, valve or cap and every joint gets numbered here.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => onSetAllWelds(ids, "shop")}>
                <Factory className="mr-1 h-3.5 w-3.5" /> All shop
              </Button>
              <Button size="sm" variant="outline" onClick={() => onSetAllWelds(ids, "field")}>
                <HardHat className="mr-1 h-3.5 w-3.5" /> All field
              </Button>
              <Button
                size="sm"
                variant={shopFabDefault ? "default" : "outline"}
                onClick={() => onSetShopFabDefault(!shopFabDefault)}
              >
                Fitting welds default to shop
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md border p-2">
                <div className="text-[11px] uppercase text-muted-foreground">Shop</div>
                <div className="text-sm font-semibold">{totals.shop}</div>
                <div className="text-[11px] text-muted-foreground">
                  {totals.shopDi.toFixed(1)} DI
                </div>
              </div>
              <div className="rounded-md border p-2">
                <div className="text-[11px] uppercase text-muted-foreground">Field</div>
                <div className="text-sm font-semibold">{totals.field}</div>
                <div className="text-[11px] text-muted-foreground">
                  {totals.fieldDi.toFixed(1)} DI
                </div>
              </div>
              <div className="rounded-md border p-2">
                <div className="text-[11px] uppercase text-muted-foreground">NDE</div>
                <div className="text-sm font-semibold">{result.ndeWelds}</div>
                <div className="text-[11px] text-muted-foreground">
                  {result.ndeDi.toFixed(1)} DI
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-[11px] uppercase text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">Weld</th>
                    <th className="p-2 text-left">Size</th>
                    <th className="p-2 text-left">Joins</th>
                    <th className="p-2 text-right">Pos.</th>
                  </tr>
                </thead>
                <tbody>
                  {welds.map((w) => (
                    <tr key={w.id} className="border-t">
                      <td className="p-2 font-mono">{w.no}</td>
                      <td className="p-2">{w.size}</td>
                      <td className="p-2 text-muted-foreground">{w.connects}</td>
                      <td className="p-2 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            onSetWeldPosition(w.id, w.position === "shop" ? "field" : "shop")
                          }
                          className={`rounded px-2 py-0.5 text-[11px] font-medium ${
                            w.position === "shop"
                              ? "bg-primary/15 text-primary"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {w.position === "shop" ? "Shop" : "Field"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Joint type: {welds[0].jointLabel}. DI = diameter inches, the unit used to price
              welding and set NDE scope.
            </p>
          </>
        )}
      </section>

      <section className="space-y-3 border-t pt-3">
        <h3 className="text-sm font-semibold">Pressure test package</h3>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Design pressure (psig)</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={test.designPressure}
              onChange={(e) => onSetTest({ designPressure: Number(e.target.value) || 0 })}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Hold time (min)</Label>
            <Input
              type="number"
              inputMode="numeric"
              value={test.holdMinutes}
              onChange={(e) => onSetTest({ holdMinutes: Number(e.target.value) || 0 })}
              className="h-8"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Test medium</Label>
          <Select
            value={test.medium}
            onValueChange={(v) => onSetTest({ medium: v as TestMedium })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEST_MEDIA.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {welded && (
          <div className="space-y-1">
            <Label className="text-xs">NDE / radiography scope</Label>
            <Select
              value={String(test.ndePercent)}
              onValueChange={(v) => onSetTest({ ndePercent: Number(v) as NdePercent })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NDE_LEVELS.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}% of welds
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">System / line no.</Label>
            <Input
              value={test.system}
              placeholder="CHW-101"
              onChange={(e) => onSetTest({ system: e.target.value })}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Tested by</Label>
            <Input
              value={test.testedBy}
              placeholder="Name"
              onChange={(e) => onSetTest({ testedBy: e.target.value })}
              className="h-8"
            />
          </div>
        </div>

        <div className="rounded-md border bg-muted/30 p-3 text-xs">
          <div className="flex items-baseline justify-between">
            <span className="text-muted-foreground">Test pressure</span>
            <span className="text-base font-semibold">{result.testPressure} psig</span>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <span className="text-muted-foreground">Gauge full scale</span>
            <span className="font-medium">
              {result.gaugeMin}–{result.gaugeMax} psi
            </span>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">{result.note}</p>
        </div>

        {test.medium === "pneumatic" && (
          <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <span>
              Pneumatic testing stores far more energy than hydro. Clear the area, step the
              pressure up in stages and hold at 25 psig for the preliminary check.
            </span>
          </div>
        )}
      </section>
    </div>
  );
};

export default WeldMapPanel;
